import { IntegrationType, NonSensitiveFlightRecordConnectedAccount } from "@nova/schemas"
import { fetchApiGateway, extractApiGatewayError } from "@nova/fetch-api-gateway"
import type { IntegrationAuthorizationResponse } from "@nova/data-facade"

export { SyncStatus } from "@nova/schemas"

export type AccountsByIntegrationType = {
    [K in IntegrationType]?: NonSensitiveFlightRecordConnectedAccount<K>[]
}

export type SyncInfo =
    | {
          status: "syncing"
          triggeredAt: Date
          filesTotal: number | null
          filesSynced: number
      }
    | { status: "synced"; triggeredAt: Date }
    | { status: "error"; triggeredAt: Date }

const buildAccountsByIntegrationType = (
    djiIntegrations: IntegrationAuthorizationResponse<"dji", IntegrationType.dji>[] | undefined,
    autelIntegrations:
        | IntegrationAuthorizationResponse<"autel", IntegrationType.autel>[]
        | undefined,
    skydioIntegrations:
        | IntegrationAuthorizationResponse<"token", IntegrationType.skydio>[]
        | undefined
): AccountsByIntegrationType => {
    const result: AccountsByIntegrationType = {}

    djiIntegrations?.forEach((authorization) => {
        const { integration: type, settings, id, userId, createdAt } = authorization
        const { nick_name, email } = settings

        result[type] ??= []
        result[type].push({
            id,
            userId,
            accountId: email,
            firstName: nick_name,
            createdAt,
            integrationType: type,
        })
    })

    autelIntegrations?.forEach((authorization) => {
        const { integration: type, settings, id, userId, createdAt } = authorization
        const { firstName, lastName, autelId } = settings

        result[type] ??= []
        result[type].push({
            id,
            userId,
            accountId: autelId,
            firstName,
            lastName,
            createdAt,
            integrationType: type,
        })
    })

    skydioIntegrations?.forEach((authorization) => {
        const { integration: type, settings, id, userId, createdAt } = authorization
        const { name, obfuscatedToken } = settings

        result[type] ??= []
        result[type].push({
            id,
            userId,
            accountId: obfuscatedToken,
            firstName: name,
            createdAt,
            integrationType: type,
        })
    })

    // Sort integrations alphabetically.
    ;(Object.keys(result) as IntegrationType[]).forEach((type) =>
        result[type]?.sort((a, b) => a.accountId.localeCompare(b.accountId))
    )

    return result
}

const createNewDjiConnection = async (
    orgId: string | undefined,
    email: string,
    password: string,
    invalidate: () => Promise<unknown>
) => {
    if (!orgId) return

    await fetchApiGateway("post /org/:orgId/integrations/dji/authorize", {
        params: { orgId },
        body: { email, password },
    })

    await invalidate()
}

const updateDjiConnection = async (
    orgId: string | undefined,
    integrationId: string,
    updates: {
        password: string
    },
    invalidate: () => Promise<unknown>
) => {
    if (!orgId) return

    await fetchApiGateway("patch /org/:orgId/integrations/dji/authorize", {
        params: { orgId },
        body: { integrationId, password: updates.password },
    })

    await invalidate()
}

const deleteDjiConnection = async (
    orgId: string | undefined,
    integrationId: string,
    invalidate: () => Promise<unknown>
) => {
    if (!orgId) return

    await fetchApiGateway("delete /org/:orgId/integrations/dji/authorize", {
        params: { orgId },
        body: { integrationId },
    })

    await invalidate()
}

const createNewAutelConnection = async (
    orgId: string | undefined,
    email: string,
    password: string,
    invalidate: () => Promise<unknown>
) => {
    if (!orgId) return

    await fetchApiGateway("post /org/:orgId/integrations/autel/authorize", {
        params: { orgId },
        body: { email, password },
    })

    await invalidate()
}

const updateAutelConnection = async (
    orgId: string | undefined,
    integrationId: string,
    updates: {
        password: string
    },
    invalidate: () => Promise<unknown>
) => {
    if (!orgId) return

    await fetchApiGateway("patch /org/:orgId/integrations/autel/authorize", {
        params: { orgId },
        body: { integrationId, password: updates.password },
    })

    await invalidate()
}

const deleteAutelConnection = async (
    orgId: string | undefined,
    integrationId: string,
    invalidate: () => Promise<unknown>
) => {
    if (!orgId) return

    await fetchApiGateway("delete /org/:orgId/integrations/autel/authorize", {
        params: { orgId },
        body: { integrationId },
    })

    await invalidate()
}

const createNewSkydioConnection = async (
    orgId: string | undefined,
    apiToken: string,
    invalidate: () => Promise<unknown>
) => {
    if (!orgId) return

    const integration = await fetchApiGateway("post /org/:orgId/integrations/skydio/authorize", {
        params: { orgId },
        body: { apiToken },
    })
    if ("error" in integration) throw integration

    await invalidate()
    return integration
}

const updateSkydioConnection = async (
    orgId: string | undefined,
    integrationId: string,
    updates: {
        apiToken: string
    },
    invalidate: () => Promise<unknown>
) => {
    if (!orgId) return

    const integration = await fetchApiGateway("patch /org/:orgId/integrations/skydio/authorize", {
        params: { orgId },
        body: { integrationId, apiToken: updates.apiToken },
    })
    if ("error" in integration) throw integration

    await invalidate()
    return integration
}

const deleteSkydioConnection = async (
    orgId: string | undefined,
    integrationId: string,
    invalidate: () => Promise<unknown>
) => {
    if (!orgId) return

    await fetchApiGateway("delete /org/:orgId/integrations/skydio/authorize", {
        params: { orgId },
        body: { integrationId },
    })

    await invalidate()
}

const fetchSyncStatuses = async ([, orgId]: [string, string]): Promise<
    Record<string, SyncInfo | undefined>
> => {
    const response = await fetchApiGateway("get /org/:orgId/integrations/syncs", {
        params: { orgId },
    })
    if (!response || !Array.isArray(response)) return {}
    return response.reduce(
        (acc, sync) => {
            const status = sync.status
            const triggeredAt = new Date(sync.timestamp)
            if (status === "syncing") {
                acc[sync.id] = {
                    status,
                    triggeredAt,
                    filesTotal: sync.filesTotal ?? null,
                    filesSynced: sync.filesSynced ?? 0,
                }
            } else {
                acc[sync.id] = { status, triggeredAt }
            }
            return acc
        },
        {} as Record<string, SyncInfo | undefined>
    )
}

const triggerDjiSync = async (
    [, orgId]: [string, string],
    {
        arg: { integrationId, invalidate },
    }: { arg: { integrationId: string; invalidate: () => Promise<unknown> } }
) => {
    try {
        await fetchApiGateway("post /org/:orgId/integrations/dji/:integrationId/sync", {
            params: { orgId, integrationId },
        })
        await invalidate()
    } catch (error) {
        const apiError = extractApiGatewayError(error)
        if (apiError) throw new Error(apiError.message)
        throw error
    }
}

const triggerAutelSync = async (
    [, orgId]: [string, string],
    {
        arg: { integrationId, invalidate },
    }: { arg: { integrationId: string; invalidate: () => Promise<unknown> } }
) => {
    try {
        await fetchApiGateway("post /org/:orgId/integrations/autel/:integrationId/sync", {
            params: { orgId, integrationId },
        })
        await invalidate()
    } catch (error) {
        const apiError = extractApiGatewayError(error)
        if (apiError) throw new Error(apiError.message)
        throw error
    }
}

const triggerSkydioSync = async (
    [, orgId]: [string, string],
    {
        arg: { integrationId, invalidate },
    }: { arg: { integrationId: string; invalidate: () => Promise<unknown> } }
) => {
    try {
        await fetchApiGateway("post /org/:orgId/integrations/skydio/:integrationId/sync", {
            params: { orgId, integrationId },
        })
        await invalidate()
    } catch (error) {
        const apiError = extractApiGatewayError(error)
        if (apiError) throw new Error(apiError.message)
        throw error
    }
}

const exports = {
    buildAccountsByIntegrationType,
    fetchSyncStatuses,

    // DJI
    createNewDjiConnection,
    updateDjiConnection,
    deleteDjiConnection,
    triggerDjiSync,

    // Autel
    createNewAutelConnection,
    updateAutelConnection,
    deleteAutelConnection,
    triggerAutelSync,

    // Skydio
    createNewSkydioConnection,
    updateSkydioConnection,
    deleteSkydioConnection,
    triggerSkydioSync,
}

export default exports
